import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AuthModal from '../../../components/auth/AuthModal';
import { useAuth } from '../../../hooks/useAuth';
import { signOut } from '../../../lib/supabase';
import { NotificationBell } from '../../../components/notifications/NotificationBell';
import { PendingProposalsBadge } from '../../../components/notifications/PendingProposalsBadge';
import { requestNotificationPermission } from '../../../hooks/useNotifications';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Request notification permission when user logs in
  useEffect(() => {
    if (user) {
      requestNotificationPermission();
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-swap-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">SwapHub</span>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/browse"
                className="text-gray-700 hover:text-teal-500 transition-colors font-medium"
              >
                Browse Items
              </Link>
              <Link
                to="/browse-vehicles"
                className="text-gray-700 hover:text-teal-500 transition-colors font-medium"
              >
                Browse Vehicles
              </Link>
              <a
                href="#how-it-works"
                className="text-gray-700 hover:text-teal-500 transition-colors font-medium"
              >
                How It Works
              </a>
              <a
                href="#pricing"
                className="text-gray-700 hover:text-teal-500 transition-colors font-medium"
              >
                Pricing
              </a>
            </div>

            <div className="flex items-center space-x-4">
              {loading ? (
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
              ) : user && profile ? (
                <>
                  <PendingProposalsBadge />
                  <NotificationBell />
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        {profile.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt={profile.username}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-teal-600 font-semibold">
                            {profile.username.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="hidden md:block font-medium text-gray-900">
                        {profile.username}
                      </span>
                      <i className="ri-arrow-down-s-line text-gray-600"></i>
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-2 border border-gray-100">
                        <Link
                          to="/profile"
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-user-line text-gray-600"></i>
                          <span className="text-gray-700">My Profile</span>
                        </Link>
                        <Link
                          to="/messages"
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-message-3-line text-gray-600"></i>
                          <span className="text-gray-700">Messages</span>
                        </Link>
                        <Link
                          to="/proposals"
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-inbox-line text-gray-600"></i>
                          <span className="text-gray-700">Proposals</span>
                        </Link>
                        <Link
                          to="/post-item"
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-add-circle-line text-gray-600"></i>
                          <span className="text-gray-700">Post Item</span>
                        </Link>
                        <hr className="my-2 border-gray-100" />
                        <button
                          onClick={handleSignOut}
                          className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors w-full text-left cursor-pointer"
                        >
                          <i className="ri-logout-box-line text-red-500"></i>
                          <span className="text-red-500">Sign Out</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="text-gray-700 hover:text-teal-500 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          window.location.reload();
        }}
      />
    </>
  );
}
